import { useState, useEffect } from 'react'
import {
  Bot,
  MessageSquare,
  Users,
  Zap,
  TrendingUp,
  Plus,
  Activity,
  Clock,
  Star,
  Settings
} from 'lucide-react'
import StatsCard from '../../components/cards/StatsCard'
import Card from '../../components/cards/Card'
import Button from '../../components/buttons/Button'
import { useChat } from '../../contexts/ChatContext'

const Dashboard = () => {
  const { chats, createNewChat } = useChat()
  const [recentActivity, setRecentActivity] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Simulate loading dashboard data
    const timer = setTimeout(() => {
      setIsLoading(false)
      setRecentActivity([
        {
          id: 1,
          type: 'chat',
          message: 'New conversation started with Customer Support Bot',
          time: '2 minutes ago',
          icon: MessageSquare
        },
        {
          id: 2,
          type: 'bot',
          message: 'AI Bot "Sales Assistant" deployed successfully',
          time: '1 hour ago',
          icon: Bot
        },
        {
          id: 3,
          type: 'analytics',
          message: 'Weekly report generated with 95% satisfaction rate',
          time: '3 hours ago',
          icon: TrendingUp
        },
        {
          id: 4,
          type: 'team',
          message: 'New team member John Doe joined the workspace',
          time: '1 day ago',
          icon: Users
        }
      ])
    }, 1500)

    return () => clearTimeout(timer)
  }, [])

  const stats = [
    {
      title: 'Total Conversations',
      value: 1247,
      change: 12.5,
      changeType: 'positive',
      icon: MessageSquare,
      iconColor: 'primary',
      description: 'This month'
    },
    {
      title: 'Active Bots',
      value: 8,
      change: 2,
      changeType: 'positive',
      icon: Bot,
      iconColor: 'accent',
      description: 'Across all channels'
    },
    {
      title: 'Response Time',
      value: '1.2s',
      change: -15.3,
      changeType: 'positive',
      icon: Zap,
      iconColor: 'success',
      description: 'Average response time'
    },
    {
      title: 'User Satisfaction',
      value: '94.2%',
      change: 3.1,
      changeType: 'positive',
      icon: Star,
      iconColor: 'warning',
      description: 'Based on feedback'
    }
  ]

  const quickActions = [
    {
      title: 'Create New Bot',
      description: 'Set up a new AI chatbot',
      icon: Bot,
      color: 'primary',
      href: '/bots/create'
    },
    {
      title: 'Start Conversation',
      description: 'Begin a new chat session',
      icon: MessageSquare,
      color: 'accent',
      onClick: () => createNewChat()
    },
    {
      title: 'View Analytics',
      description: 'Check performance metrics',
      icon: TrendingUp,
      color: 'success',
      href: '/analytics'
    },
    {
      title: 'Bot Settings',
      description: 'Configure your bots',
      icon: Settings,
      color: 'warning',
      href: '/bots/settings'
    }
  ]

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
            <p className="text-secondary-400">Welcome back! Here's what's happening with your AI chatbots.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="space-y-3">
                <div className="h-4 bg-secondary-700 rounded w-3/4"></div>
                <div className="h-8 bg-secondary-700 rounded w-1/2"></div>
                <div className="h-3 bg-secondary-700 rounded w-1/3"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
          <p className="text-secondary-400">Welcome back! Here's what's happening with your AI chatbots.</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <StatsCard
            key={index}
            title={stat.title}
            value={stat.value}
            change={stat.change}
            changeType={stat.changeType}
            icon={stat.icon}
            iconColor={stat.iconColor}
            description={stat.description}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
              <Button variant="ghost" size="sm">
                View All
              </Button>
            </div>

            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-secondary-700/50 transition-colors">
                  <div className="flex-shrink-0 p-2 bg-secondary-700 rounded-lg">
                    <activity.icon className="w-4 h-4 text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm">{activity.message}</p>
                    <p className="text-secondary-500 text-xs mt-1">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div>
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-white mb-6">Quick Actions</h3>

            <div className="space-y-3">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-700/50 transition-colors text-left group"
                >
                  <div className={`p-2 rounded-lg bg-${action.color}-500/20`}>
                    <action.icon className={`w-4 h-4 text-${action.color}-400`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-medium text-sm group-hover:text-primary-400 transition-colors">
                      {action.title}
                    </h4>
                    <p className="text-secondary-400 text-xs mt-1">
                      {action.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>

    </div>
  )
}

export default Dashboard
